import { create } from 'zustand';
import type { ForecastData } from '../core/forecast';
import { fetchForecast } from '../services/openMeteo';
import type { Place } from './settings';

export interface ForecastState {
  data: ForecastData | null;
  /** Coordinates the loaded data belongs to. */
  dataFor: Place | null;
  updatedAt: number | null;
  /** 'stale' = fetch failed, showing the last cached forecast for this place. */
  status: 'idle' | 'loading' | 'ready' | 'stale' | 'error';
  error: string | null;
  load: (loc: Place) => Promise<void>;
  /** Whether a foreground wake should reload: the app can sit suspended in
   * memory for days, and `load` otherwise only runs when the place changes. */
  isDue: () => boolean;
}

let generation = 0; // drop out-of-date responses when the location changes mid-fetch

// v3: adds hourly code/isDay/rh/dew + minutely nowcast, and stores timeIndex
// as entries — a raw Map JSON-stringifies to {}, so v2 caches restored with a
// broken (empty-object) timeIndex that crashed block scoring on the stale path.
const CACHE_KEY = 'blockcast.v3.forecastCache';
// Beyond this age the cached days no longer line up with "today" — worse than no data.
const CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000;
// Under this age the cache is served instead of refetching. The forecast
// itself only updates hourly upstream, so a relaunch a few minutes later was
// spending four billable requests to redraw the same numbers.
const CACHE_FRESH_MS = 20 * 60 * 1000;

interface CachedForecast {
  data: ForecastData;
  dataFor: Place;
  updatedAt: number;
}

function saveCache(entry: CachedForecast) {
  try {
    const data = { ...entry.data, timeIndex: [...entry.data.timeIndex] };
    localStorage.setItem(CACHE_KEY, JSON.stringify({ ...entry, data }));
  } catch {
    // quota exceeded or private mode — the cache is best-effort
  }
}

function readCache(loc: Place): CachedForecast | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const entry = JSON.parse(raw) as CachedForecast;
    const idx = entry.data.timeIndex as unknown;
    entry.data.timeIndex = new Map(Array.isArray(idx) ? (idx as [string, number][]) : []);
    const samePlace =
      Math.abs(entry.dataFor.lat - loc.lat) < 0.01 && Math.abs(entry.dataFor.lon - loc.lon) < 0.01;
    if (!samePlace || Date.now() - entry.updatedAt > CACHE_MAX_AGE_MS) return null;
    return entry;
  } catch {
    return null;
  }
}

export const useForecast = create<ForecastState>()((set, get) => ({
  data: null,
  dataFor: null,
  updatedAt: null,
  status: 'idle',
  error: null,

  // 'stale' and 'error' are due too, so coming back to the app retries a
  // failed fetch; 'loading' is not, as one wake fires several events at once
  isDue: () => {
    const { status, updatedAt } = get();
    if (status === 'loading') return false;
    return status !== 'ready' || updatedAt == null || Date.now() - updatedAt >= CACHE_FRESH_MS;
  },

  load: async (loc) => {
    const gen = ++generation;
    // still-fresh cache: serve it and make no requests at all. `dataFor` is
    // the place that was asked for, not the cached one — they agree to 0.01°.
    const fresh = readCache(loc);
    if (fresh && Date.now() - fresh.updatedAt < CACHE_FRESH_MS) {
      set({
        data: fresh.data,
        dataFor: loc,
        updatedAt: fresh.updatedAt,
        status: 'ready',
        error: null,
      });
      return;
    }
    set({ status: 'loading', error: null });
    try {
      const data = await fetchForecast(loc.lat, loc.lon);
      if (gen !== generation) return; // a newer fetch superseded this one
      const updatedAt = Date.now();
      set({ data, dataFor: loc, updatedAt, status: 'ready' });
      saveCache({ data, dataFor: loc, updatedAt });
    } catch (err) {
      if (gen !== generation) return;
      const error = err instanceof Error ? err.message : String(err);
      const cached = readCache(loc);
      if (cached) {
        set({
          data: cached.data,
          dataFor: cached.dataFor,
          updatedAt: cached.updatedAt,
          status: 'stale',
          error,
        });
      } else {
        set({ status: 'error', error });
      }
    }
  },
}));
